/**
 * The worker's copy of `getJobClientContext` — who the job is for, the
 * on-site contact and (UX 1C) the object's arrival guide — kept on the
 * device so it is there on the first morning without network.
 *
 * Moved here from WorkerTodayCard (UX 1A) so the Today card, the job view's
 * contact card and the arrival guide read ONE stored copy and ONE request:
 * concurrent readers of the same job share the in-flight call. Tomorrow's
 * jobs are fetched ahead (`usePrefetchJobContacts`) so the guide is already
 * stored when the worker leaves home.
 *
 * `fresh: false` = showing the stored copy (offline, or the call failed) —
 * callers say when it was loaded ("viimati laaditud HH:MM").
 */
import type { JobClientContext } from '@rendimees24/shared-types';
import { useEffect, useState } from 'react';

export interface CachedContact {
  ctx: JobClientContext;
  /** ISO instant of the server answer. */
  at: string;
}

const contactKey = (jobId: string) => `rm24.worker.jobContact.${jobId}`;

export function readCachedContact(jobId: string): CachedContact | null {
  try {
    const raw = window.localStorage.getItem(contactKey(jobId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedContact;
    return parsed?.ctx && typeof parsed.at === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Drop the stored contact + arrival guide for a job — the server said the
 * worker no longer has it (assignment removed, job gone). Called from
 * `services/worker.ts` so an offline open cannot show a stale contact.
 */
export function forgetCachedContact(jobId: string): void {
  try {
    window.localStorage.removeItem(contactKey(jobId));
  } catch {
    /* storage unavailable — nothing stored either */
  }
}

function writeCachedContact(jobId: string, value: CachedContact): void {
  try {
    window.localStorage.setItem(contactKey(jobId), JSON.stringify(value));
  } catch {
    /* quota / private mode — the live answer is still shown */
  }
}

/** One request per job at a time, whoever asks. */
const inFlight = new Map<string, Promise<CachedContact | null>>();

/** Fetch, store and return the fresh copy; null when it could not be loaded. */
export function loadJobContact(jobId: string): Promise<CachedContact | null> {
  const running = inFlight.get(jobId);
  if (running) return running;
  // Loaded on use, not with the dashboard bundle; any failure is a null.
  const p = import('../services/jobs')
    .then(({ getJobClientContext }) => getJobClientContext(jobId))
    .then((ctx) => {
      if (!ctx) return null;
      const value: CachedContact = { ctx, at: new Date().toISOString() };
      writeCachedContact(jobId, value);
      return value;
    })
    .catch(() => null)
    .finally(() => inFlight.delete(jobId));
  inFlight.set(jobId, p);
  return p;
}

export function useJobContact(jobId: string) {
  const [state, setState] = useState<{ value: CachedContact | null; fresh: boolean }>(() => ({
    value: readCachedContact(jobId),
    fresh: false,
  }));
  useEffect(() => {
    let live = true;
    setState({ value: readCachedContact(jobId), fresh: false });
    void loadJobContact(jobId).then((value) => {
      // Not authorised or offline — keep the stored copy.
      if (live && value) setState({ value, fresh: true });
    });
    return () => {
      live = false;
    };
  }, [jobId]);
  return state;
}

/** Store tomorrow's contacts and guides now, while there is network. */
export function usePrefetchJobContacts(jobIds: readonly string[]): void {
  const key = [...new Set(jobIds)].sort().join(',');
  useEffect(() => {
    if (!key) return;
    for (const id of key.split(',')) void loadJobContact(id);
  }, [key]);
}
